import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const EmailSettingsSection = ({ userProfile, expandedSection, onAccordionChange, setSuccess, setError, currentUser, isMobile }) => {
  const [emailSettings, setEmailSettings] = useState({
    newBookingNotifications: userProfile.emailSettings?.newBookingNotifications ?? true,
    appointmentReminders: userProfile.emailSettings?.appointmentReminders ?? false,
    reviewRequests: userProfile.emailSettings?.reviewRequests ?? false
  });
  const [googleMyBusinessUrl, setGoogleMyBusinessUrl] = useState(userProfile.googleMyBusinessUrl || '');

  const handleEmailSettingChange = async (setting, value) => {
    try {
      const newSettings = { ...emailSettings, [setting]: value };
      setEmailSettings(newSettings);

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        emailSettings: newSettings,
        updatedAt: new Date()
      });

      setSuccess('Paramètres d\'email mis à jour avec succès');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating email settings:', error);
      setError('Erreur lors de la mise à jour des paramètres d\'email');
      setEmailSettings(prev => ({ ...prev, [setting]: !value }));
    }
  };

  const handleGoogleUrlChange = async () => {
    if (googleMyBusinessUrl && !validateGoogleUrl(googleMyBusinessUrl)) {
      setError('Veuillez entrer une URL Google Maps valide');
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      let updateData = {
        googleMyBusinessUrl: googleMyBusinessUrl.trim(),
        updatedAt: new Date()
      };

      // Si une URL est fournie, récupérer les avis Google
      if (googleMyBusinessUrl.trim()) {
        try {
          // Extraire le place ID
          const extractResponse = await fetch('https://us-central1-omnibeauty-d032e.cloudfunctions.net/extractPlaceId', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ url: googleMyBusinessUrl.trim() })
          });
          
          if (extractResponse.ok) {
            const extractData = await extractResponse.json();
            const placeId = extractData.placeId;
            
            // Récupérer les avis Google
            const reviewsResponse = await fetch(`https://us-central1-omnibeauty-d032e.cloudfunctions.net/getGoogleReviews/${placeId}`);
            if (reviewsResponse.ok) {
              const reviewsData = await reviewsResponse.json();
              
              // Mettre à jour les données avec les vrais avis Google
              updateData = {
                ...updateData,
                placeId: placeId,
                rating: reviewsData.rating || 0,
                reviewCount: reviewsData.user_ratings_total || 0,
                googleReviews: reviewsData.reviews || []
              };
            }
          }
        } catch (reviewError) {
          console.error('Error fetching Google reviews:', reviewError);
          // Continue même si la récupération des avis échoue
        }
      }

      await updateDoc(userRef, updateData);

      setSuccess('URL Google My Business mise à jour avec succès' + (updateData.rating ? ` - ${updateData.reviewCount} avis récupérés (${updateData.rating}/5)` : ''));
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Error updating Google URL:', error);
      setError('Erreur lors de la mise à jour de l\'URL Google');
    }
  };

  const validateGoogleUrl = (url) => {
    if (!url) return true;
    const googleBusinessPattern = /^https:\/\/(www\.)?google\.(com|fr)\/maps\/place\//;
    return googleBusinessPattern.test(url);
  };

  const colors = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#F5F5DC',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB'
  };

  const content = (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 4 } }}>
      {!isMobile && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary,
            mb: { xs: 2, md: 3 },
            fontSize: { xs: '1.125rem', md: '1.25rem' }
          }}
        >
          Paramètres d'Email
        </Typography>
      )}

      <Typography
        variant="body2"
        sx={{
          color: colors.secondary,
          mb: { xs: 2, md: 3 },
          lineHeight: 1.6,
          fontSize: { xs: '0.875rem', md: '1rem' }
        }}
      >
        Configurez les notifications automatiques par email
      </Typography>

      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={emailSettings.newBookingNotifications}
                onChange={(e) => handleEmailSettingChange('newBookingNotifications', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Notifications de nouvelles réservations
              </Typography>
            }
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              ml: 4, 
              mt: 0.5,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          >
            Recevez un email à chaque nouvelle réservation de client
          </Typography>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={emailSettings.appointmentReminders}
                onChange={(e) => handleEmailSettingChange('appointmentReminders', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Rappels automatiques aux clients
              </Typography>
            }
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              ml: 4, 
              mt: 0.5,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          >
            Envoyer un rappel par email aux clients 24h avant leur rendez-vous
          </Typography>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={emailSettings.reviewRequests}
                onChange={(e) => handleEmailSettingChange('reviewRequests', e.target.checked)}
                disabled={!googleMyBusinessUrl}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Demandes d'avis Google
              </Typography>
            }
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              ml: 4, 
              mt: 0.5,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          >
            Demander aux clients de laisser un avis 24h après leur visite
            {!googleMyBusinessUrl && (
              <Typography 
                component="span" 
                sx={{ 
                  display: 'block', 
                  fontWeight: 500,
                  color: 'warning.main',
                  mt: 0.5
                }}
              >
                Vous devez d'abord configurer votre URL Google My Business ci-dessous
              </Typography>
            )}
          </Typography>
        </Box>

        <Divider sx={{ my: 3, borderColor: colors.border }} />
        
        <Box>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 600,
              color: colors.primary,
              mb: 2,
              fontSize: { xs: '1rem', md: '1.125rem' }
            }}
          >
            Google My Business
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              mb: 2,
              fontSize: { xs: '0.875rem', md: '1rem' }
            }}
          >
            Pour activer les demandes d'avis automatiques, ajoutez l'URL de votre fiche Google My Business.
          </Typography>

          <TextField
            fullWidth
            label="URL Google My Business"
            value={googleMyBusinessUrl}
            onChange={(e) => setGoogleMyBusinessUrl(e.target.value)}
            placeholder="https://www.google.com/maps/place/votre-salon"
            error={googleMyBusinessUrl && !validateGoogleUrl(googleMyBusinessUrl)}
            helperText={
              googleMyBusinessUrl && !validateGoogleUrl(googleMyBusinessUrl)
                ? "Veuillez entrer une URL Google Maps valide"
                : "Copiez l'URL de votre fiche Google My Business depuis Google Maps"
            }
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&.Mui-focused fieldset': {
                  borderColor: colors.primary,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: colors.primary,
              },
              '& .MuiFormHelperText-root': {
                fontSize: { xs: '0.75rem', md: '0.875rem' }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={handleGoogleUrlChange}
            disabled={googleMyBusinessUrl && !validateGoogleUrl(googleMyBusinessUrl)}
            fullWidth
            sx={{
              borderColor: colors.primary,
              color: colors.primary,
              py: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.875rem', md: '1rem' },
              fontWeight: 600,
              borderRadius: 2,
              textTransform: 'none',
              mb: 2,
              '&:hover': {
                borderColor: colors.primary,
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            Sauvegarder l'URL
          </Button>
        </Box>

        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontSize: { xs: '0.875rem', md: '1rem' }
            }
          }}
        >
          <Typography variant="body2">
            <strong>Informations importantes :</strong>
            <br />• Les emails sont envoyés automatiquement par BookingBarber
            <br />• Les rappels sont envoyés exactement 24h avant le rendez-vous
            <br />• Les demandes d'avis sont envoyées 24h après la fin du rendez-vous
          </Typography>
        </Alert>
      </Stack>
    </Box>
  );

  if (isMobile) {
    return (
      <Accordion 
        expanded={expandedSection === 'email'} 
        onChange={onAccordionChange('email')}
        elevation={0}
        sx={{ 
          '&:before': { display: 'none' },
          borderRadius: '0 !important'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            px: 3,
            py: 2,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              '&.Mui-expanded': {
                margin: 0
              }
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: colors.primary,
              fontSize: '1.125rem'
            }}
          >
            Paramètres d'Email
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {content}
        </AccordionDetails>
      </Accordion>
    );
  }

  return content;
};

export default EmailSettingsSection;